import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { notasVentaApi, clientesApi } from '../../api/notasVenta'
import { listasApi, articuloPreciosApi } from '../../api/listaPrecios'
import { useAuth } from '../../context/AuthContext'
import { hoyISO } from '../../utils/fecha'
import useDebounce from '../../hooks/useDebounce'
import { colors, font, text } from '../../design/tokens'
import SelectorArticulosModal from '../../components/SelectorArticulosModal'
import { Card, Seccion, Buscador, Titulo, fmtMoney, BotonPrimario, ErrorBox } from './comunes'
import { listaParaBuscar } from '../../utils/listaVenta'

// Alta de NV desde el celular. Mismo contrato que el form de escritorio
// (POST /notas-venta) con los mismos defaults (CONTADO / depósito 100 /
// vendedor = quien carga). Jerarquía de lista, igual que el escritorio:
// la lista del USUARIO si tiene una asignada (fija); si no, el vendedor
// ELIGE la lista (por defecto la del cliente). La lista elegida viaja en la
// NV: define el canal de la venta.
//
// Los artículos se cargan con EL selector único (SelectorArticulosModal,
// selección múltiple). El precio del renglón se trae por artículo
// (GET /articulos/{id}/precios, todas sus listas) en vez de bajar la lista
// entera: en el celular no se cargan 20.000 precios para vender 3 botellas.
// Si el artículo no está en la lista, el precio queda vacío y lo escribe el
// vendedor (nunca se inventa). Cambiar la lista es la decisión del operador
// de recotizar: se recalculan los renglones, salvo los que editó a mano.

export default function MovilNvNueva() {
  const navigate = useNavigate()
  const { user } = useAuth()

  const [cliente, setCliente] = useState(null)
  const [items, setItems] = useState([])
  const [observaciones, setObservaciones] = useState('')
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState('')
  const [listas, setListas] = useState([])
  const [listaElegida, setListaElegida] = useState('')
  const [selector, setSelector] = useState(false)
  const [avisoLista, setAvisoLista] = useState('')

  useEffect(() => {
    listasApi.listar().then(({ data }) => setListas(Array.isArray(data) ? data : (data.data || []))).catch(() => setListas([]))
  }, [])

  const userLista = user?.lista_precios || null
  // Sin lista de usuario manda la ELEGIDA (arranca en la del cliente, si esa
  // lista existe en el maestro — hay fichas con listas que ya no existen).
  const listaEfectiva = userLista || listaElegida || null
  // La NV y el cliente guardan el CÓDIGO (LISTA1); /articulos/{id}/precios
  // responde por lista_id — se cruza por el maestro de listas.
  const listaObj = listas.find((l) => [l.codigo, l.codigo_presea, l.nombre].includes(listaEfectiva)) || null
  const precioEnLista = (precios, lista) => {
    if (!lista) return null
    const p = (precios || []).find((x) => x.lista_id === lista.id)
    return p ? String(parseFloat(p.precio)) : null
  }

  const elegirCliente = (c) => {
    setCliente(c)
    setListaElegida(listas.find((l) => l.codigo === c?.lista_precios)?.codigo || '')
    setAvisoLista('')
  }

  // Cambiar la lista = recotizar (decisión del operador). Los renglones con
  // precio escrito a mano no se tocan.
  const cambiarLista = (codigo) => {
    setListaElegida(codigo)
    const nueva = listas.find((l) => l.codigo === codigo) || null
    let manuales = 0
    setItems((prev) => prev.map((it) => {
      if (it.precioManual) { manuales++; return it }
      const p = precioEnLista(it.precios, nueva)
      return { ...it, precio_lista: p ?? '', fueraDeLista: p == null }
    }))
    setAvisoLista(items.length
      ? `Precios recalculados con ${nueva?.nombre || codigo}${manuales ? ' (salvo los que escribiste a mano)' : ''}.`
      : '')
  }

  const agregarArticulos = async (seleccion) => {
    const yaEstaban = new Set(items.map((it) => it.articulo_id))
    setItems((prev) => {
      const out = [...prev]
      for (const { articulo, cantidad } of seleccion) {
        const i = out.findIndex((it) => it.articulo_id === articulo.id)
        if (i >= 0) {
          out[i] = { ...out[i], cantidad: (parseInt(out[i].cantidad) || 0) + (cantidad || 1) }
        } else {
          out.push({ articulo_id: articulo.id, articulo, cantidad: cantidad || 1, precio_lista: '', cargandoPrecio: true, precios: [], precioManual: false })
        }
      }
      return out
    })
    await Promise.all(seleccion.filter(({ articulo }) => !yaEstaban.has(articulo.id)).map(async ({ articulo }) => {
      try {
        const { data } = await articuloPreciosApi.listar(articulo.id)
        const precios = Array.isArray(data) ? data : []
        setItems((prev) => prev.map((it) => {
          if (it.articulo_id !== articulo.id || !it.cargandoPrecio) return it
          const p = precioEnLista(precios, listaObj)
          return { ...it, precios, precio_lista: p ?? '', cargandoPrecio: false, fueraDeLista: p == null }
        }))
      } catch {
        setItems((prev) => prev.map((it) => it.articulo_id === articulo.id && it.cargandoPrecio ? { ...it, cargandoPrecio: false, fueraDeLista: true } : it))
      }
    }))
  }

  const setItem = (articuloId, patch) =>
    setItems((prev) => prev.map((it) => (it.articulo_id === articuloId ? { ...it, ...patch } : it)))

  const quitarItem = (articuloId) =>
    setItems((prev) => prev.filter((it) => it.articulo_id !== articuloId))

  const total = items.reduce((acc, it) => acc + (parseFloat(it.precio_lista) || 0) * (parseInt(it.cantidad) || 0), 0)
  const listo = cliente && listaEfectiva && items.length > 0 && items.every((it) => parseInt(it.cantidad) > 0 && parseFloat(it.precio_lista) > 0)

  const guardar = async () => {
    if (!listo || guardando) return
    setGuardando(true)
    setError('')
    try {
      const { data } = await notasVentaApi.crear({
        cliente_id: cliente.id,
        vendedor_id: user?.id || null,
        vendedor_codigo: user?.codigo_vendedor || null,
        // La lista con la que se cotizó ESTA venta: define su canal.
        lista_precios: listaEfectiva,
        fecha: hoyISO(),
        condicion_venta: 1,   // CONTADO, mismo default que el form de escritorio
        deposito: 100,
        observaciones: observaciones.trim() || null,
        descuento_gral: 0,
        items: items.map((it) => ({
          articulo_id: it.articulo_id,
          cantidad: parseInt(it.cantidad),
          precio_lista: parseFloat(it.precio_lista),
          descuento: 0,
        })),
      })
      navigate(`/m/ventas/${data.id}`, { replace: true })
    } catch (err) {
      const data = err.response?.data
      const detalle = data?.errors ? Object.values(data.errors).flat().join(' · ') : null
      setError(detalle || data?.message || 'No se pudo crear la nota de venta.')
      setGuardando(false)
    }
  }

  return (
    <div>
      <Titulo>Nueva venta</Titulo>

      <ErrorBox>{error}</ErrorBox>

      {/* ── Cliente ── */}
      <Seccion titulo="Cliente" style={{ marginTop: 0 }}>
        {cliente ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: font.sans, fontWeight: 600, fontSize: 15, color: colors.text }}>{cliente.nombre}</div>
              <div style={{ fontSize: text.sec, color: colors.textSec, marginTop: 2 }}>
                {cliente.codigo_presea ? <span style={monoSec}>Nº {cliente.codigo_presea}</span> : 'Sin código Presea'}
                {cliente.cuit ? <span style={{ ...monoSec, marginLeft: 8 }}>{cliente.cuit}</span> : null}
                {cliente.lista_precios && <span style={{ marginLeft: 8 }}>· lista del cliente {cliente.lista_precios}</span>}
              </div>
            </div>
            <button style={btnGhost} onClick={() => { elegirCliente(null); setItems([]) }}>Cambiar</button>
          </div>
        ) : (
          <BuscadorCliente onSelect={elegirCliente} />
        )}
      </Seccion>

      {/* ── Lista de precios ── */}
      {cliente && (
        <Seccion titulo="Lista de precios">
          {userLista ? (
            <p style={{ ...hint, margin: 0, color: colors.textSec, fontSize: 13.5 }}>
              <b style={{ color: colors.text }}>{listaObj?.nombre || userLista}</b> — asignada a tu usuario.
            </p>
          ) : (
            <>
              <select value={listaElegida} onChange={(e) => cambiarLista(e.target.value)} style={select}>
                <option value="">Elegí la lista…</option>
                {listas.map((l) => (
                  <option key={l.id} value={l.codigo}>
                    {l.nombre}{l.codigo === cliente.lista_precios ? ' (la del cliente)' : ''}
                  </option>
                ))}
              </select>
              {!listaElegida && cliente.lista_precios && !listas.some((l) => l.codigo === cliente.lista_precios) && (
                <p style={hint}>La lista de la ficha ({cliente.lista_precios}) no existe en el maestro — elegí con cuál se vende.</p>
              )}
              {avisoLista && <p style={hint}>{avisoLista}</p>}
            </>
          )}
        </Seccion>
      )}

      {/* ── Artículos ── */}
      {cliente && listaEfectiva && (
        <Seccion titulo={`Artículos${items.length ? ` (${items.length})` : ''}`}>
          <button style={btnAgregar} onClick={() => setSelector(true)}>＋ Agregar artículos</button>

          {items.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
              {items.map((it) => (
                <div key={it.articulo_id} style={renglon}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: font.sans, fontSize: 13.5, fontWeight: 600, color: colors.text }}>
                        {it.articulo.descripcion}
                      </div>
                      <div style={{ ...monoSec, marginTop: 2 }}>
                        {it.articulo.codigo_alfa || it.articulo.codigo_presea}
                        {it.fueraDeLista && !it.cargandoPrecio && !it.precioManual && (
                          <span style={{ fontFamily: font.sans, color: colors.warn, marginLeft: 8 }}>fuera de la lista — cargá el precio</span>
                        )}
                        {it.precioManual && (
                          <span style={{ fontFamily: font.sans, color: colors.textMeta, marginLeft: 8 }}>precio a mano</span>
                        )}
                      </div>
                    </div>
                    <button onClick={() => quitarItem(it.articulo_id)} style={btnQuitar} aria-label="Quitar">✕</button>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                    <Stepper
                      value={parseInt(it.cantidad) || 0}
                      onChange={(v) => setItem(it.articulo_id, { cantidad: v })}
                    />
                    <span style={{ flex: 1 }} />
                    <span style={{ fontSize: text.sec, color: colors.textSec }}>$</span>
                    <input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      value={it.precio_lista}
                      placeholder={it.cargandoPrecio ? '…' : 'precio'}
                      onChange={(e) => setItem(it.articulo_id, { precio_lista: e.target.value, precioManual: true })}
                      style={inputPrecio}
                    />
                  </div>
                  <div style={{ textAlign: 'right', marginTop: 6, ...monoSec, fontWeight: 700, fontSize: 13, color: colors.text }}>
                    {fmtMoney((parseFloat(it.precio_lista) || 0) * (parseInt(it.cantidad) || 0))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Seccion>
      )}

      {selector && (
        <SelectorArticulosModal
          lista={listaParaBuscar(listaEfectiva)}
          clienteId={cliente?.id}
          onConfirm={agregarArticulos}
          onClose={() => setSelector(false)}
        />
      )}

      {/* ── Observaciones + total + guardar ── */}
      {cliente && items.length > 0 && (
        <>
          <Seccion titulo="Observaciones">
            <textarea
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
              placeholder="Opcional — notas para el pedido…"
              rows={2}
              style={textarea}
            />
          </Seccion>

          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', padding: '14px 4px 8px' }}>
            <span style={{ fontFamily: font.sans, fontWeight: 700, fontSize: 15, color: colors.text }}>TOTAL</span>
            <span style={{ fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', fontWeight: 700, fontSize: 18, color: colors.text }}>
              {fmtMoney(total)}
            </span>
          </div>

          <BotonPrimario onClick={guardar} disabled={!listo || guardando} style={{ opacity: !listo || guardando ? 0.5 : 1 }}>
            {guardando ? 'Guardando…' : '💾 Crear la venta (borrador)'}
          </BotonPrimario>
          <p style={{ fontSize: 12, color: colors.textMeta, textAlign: 'center', margin: '8px 0 0', fontFamily: font.sans }}>
            Se crea en borrador — después la procesás desde el detalle.
          </p>
        </>
      )}
    </div>
  )
}

// ─── Buscador de clientes ────────────────────────────────────────────

function BuscadorCliente({ onSelect }) {
  const [q, setQ] = useState('')
  const qBuscar = useDebounce(q, 350)
  const [resultados, setResultados] = useState([])
  const [buscando, setBuscando] = useState(false)
  const reqId = useRef(0)

  useEffect(() => {
    if (qBuscar.trim().length < 2) { setResultados([]); return }
    const id = ++reqId.current
    setBuscando(true)
    clientesApi.buscar(qBuscar).then(({ data }) => {
      if (id !== reqId.current) return
      setResultados(data.data || data || [])
    }).catch(() => {
      if (id === reqId.current) setResultados([])
    }).finally(() => { if (id === reqId.current) setBuscando(false) })
  }, [qBuscar])

  return (
    <div>
      <Buscador value={q} onChange={setQ} placeholder="Nombre, CUIT o código del cliente…" />
      {buscando && <p style={hint}>Buscando…</p>}
      {!buscando && q.trim().length >= 2 && resultados.length === 0 && <p style={hint}>Sin resultados.</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
        {resultados.slice(0, 15).map((c) => (
          <Card key={c.id} onClick={() => onSelect(c)} style={{ padding: '10px 12px' }}>
            <div style={{ fontFamily: font.sans, fontWeight: 600, fontSize: 14, color: colors.text }}>{c.nombre}</div>
            <div style={{ ...monoSec, marginTop: 2 }}>
              {c.codigo_presea ? `Nº ${c.codigo_presea}` : <span style={{ fontFamily: font.sans }}>sin código</span>}
              {c.cuit ? ` · ${c.cuit}` : ''}
              {c.localidad ? <span style={{ fontFamily: font.sans }}> · {c.localidad}</span> : null}
            </div>
          </Card>
        ))}
      </div>
    </div>
  )
}

// ─── Stepper de cantidad (targets táctiles grandes) ──────────────────

function Stepper({ value, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', border: `1px solid ${colors.border}`, borderRadius: 10, overflow: 'hidden' }}>
      <button onClick={() => onChange(Math.max(1, value - 1))} style={stepBtn} aria-label="Menos">−</button>
      <input
        type="number"
        inputMode="numeric"
        min="1"
        value={value || ''}
        onChange={(e) => onChange(Math.max(0, parseInt(e.target.value) || 0))}
        style={stepInput}
      />
      <button onClick={() => onChange(value + 1)} style={stepBtn} aria-label="Más">+</button>
    </div>
  )
}

// ─── Estilos ─────────────────────────────────────────────────────────

const monoSec = { fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', fontSize: 12, color: colors.textSec }
const hint = { fontSize: 12.5, color: colors.textMeta, margin: '8px 2px 0', fontFamily: font.sans }
const renglon = { border: `1px solid ${colors.borderSoft}`, borderRadius: 12, padding: '10px 12px', background: colors.bgSoft }
const btnGhost = {
  border: `1px solid ${colors.border}`, background: colors.bg, color: colors.textSec,
  borderRadius: 10, padding: '8px 12px', fontSize: 12.5, fontWeight: 600, fontFamily: font.sans, cursor: 'pointer', flexShrink: 0,
}
// Target táctil ≥40px: es la acción destructiva del renglón, se opera a pulgar
const btnQuitar = {
  border: 'none', background: 'transparent', color: colors.textMeta, fontSize: 16,
  cursor: 'pointer', width: 40, height: 40, display: 'flex', alignItems: 'center',
  justifyContent: 'center', padding: 0, flexShrink: 0, margin: '-8px -8px 0 0',
}
const stepBtn = {
  border: 'none', background: colors.bg, color: colors.text, fontSize: 18, fontWeight: 600,
  width: 40, height: 40, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
}
const stepInput = {
  width: 44, height: 40, border: 'none', borderLeft: `1px solid ${colors.borderSoft}`, borderRight: `1px solid ${colors.borderSoft}`,
  textAlign: 'center', fontSize: 15, fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', color: colors.text,
  outline: 'none', background: colors.bg, boxSizing: 'border-box',
}
const inputPrecio = {
  width: 110, padding: '9px 10px', border: `1px solid ${colors.border}`, borderRadius: 10,
  fontSize: 14.5, fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', color: colors.text,
  textAlign: 'right', outline: 'none', background: colors.bg, boxSizing: 'border-box',
}
const select = {
  width: '100%', boxSizing: 'border-box', padding: '11px 12px', border: `1px solid ${colors.border}`,
  borderRadius: 10, fontSize: 16, fontFamily: font.sans, color: colors.text, background: colors.bg, outline: 'none',
}
const btnAgregar = {
  width: '100%', boxSizing: 'border-box', padding: '12px', border: `1.5px dashed ${colors.primary}`, borderRadius: 12,
  background: colors.linkBg, color: colors.link, fontSize: 15, fontWeight: 700, fontFamily: font.sans, cursor: 'pointer',
}
const textarea = {
  width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: `1px solid ${colors.border}`,
  borderRadius: 10, fontSize: 14, fontFamily: font.sans, color: colors.text, outline: 'none', resize: 'vertical',
}
